import { createColumnHelper } from "@tanstack/react-table";
import { InventoryTable } from "../../interfaces/app_types";
import { getDateFromString } from "../funcs/time";
import InventoryAddDeleteButton from "../../components/inventory/InventoryEditDeleteButton";
import { fontColorBasedonBg, hexToRgba } from "../funcs/common";
import InventoryHistoryButton from "../../components/inventory/InventoryHistoryButton";
import InventoryInOutButton from "../../components/inventory/InventoryInOutButton";

const columnHelper = createColumnHelper<InventoryTable>();

const getStockColors = (current: number, safety: number) => {
  const greenColor = "#28a745"; // Bootstrap success color
  const redColor = "#dc3545"; // Bootstrap danger color
  const orangeColor = "#fd7e14"; // Bootstrap warning color for medium risk
  const blackColor = "#000000"; // Black for dark backgrounds
  const yellowColor = "#ffc107"; // Bootstrap warning color
  let currentColor = "#FFFFFF"; // Default color for light backgrounds

  if (current >= safety) {
    currentColor = greenColor;
  } else if ((safety - current) / safety < 0.2) {
    currentColor = yellowColor;
  } else if ((safety - current) / safety < 0.5) {
    currentColor = orangeColor;
  } else if ((safety - current) / safety < 0.8) {
    currentColor = redColor;
  } else {
    currentColor = blackColor;
  }

  return {
    "--item-background-color": hexToRgba(currentColor, 0.7),
    "--item-color": fontColorBasedonBg(currentColor),
  };
};

const trueFalseIcons = (value: boolean) => {
  return value ? (
    <i className="d-flex justify-content-center bi bi-check-square-fill text-success"></i>
  ) : (
    <i className="d-flex justify-content-center bi bi-x-square-fill text-danger"></i>
  );
};
export const InventoryColumns = [
  columnHelper.accessor((row) => row?.part_id, {
    header: "ID",
    enableColumnFilter: false,
    cell: (props) => (
      <div
        className={`${
          props.row.original?.discontinued ? "discontinued-item-row" : ""
        }`}
      >
        {props.row.original?.part_id}
      </div>
    ),
  }),
  columnHelper.accessor((row) => row?.part_name, {
    header: "Part Name",
    cell: (info) => info.getValue(),
  }),
  columnHelper.accessor((row) => row?.parties, {
    id: "Parties",
    filterFn: (row, columnId, filterValue): any => {
      let a = "";
      row?.original?.parties.map((party) => (a += " " + party.label));
      return a.toLowerCase().includes(filterValue.toLowerCase());
    },
    cell: (info) => (
      <div className="d-flex flex-wrap gap-1">
        {info.getValue()?.map((party, i) => (
          <div
            className="custom-color-label parties-label"
            key={i}
            style={
              {
                "--item-background-color": hexToRgba(party.color, 0.7),
                "--item-color": fontColorBasedonBg(party.color),
              } as React.CSSProperties
            }
          >
            {party.label}
          </div>
        ))}
      </div>
    ),
  }),
  columnHelper.accessor((row) => row?.product_line, {
    id: "Product Line",
    cell: (info) => info.getValue(),
  }),
  columnHelper.accessor((row) => row?.drawing, {
    id: "Drawing",
    cell: (info) => info.getValue(),
  }),
  columnHelper.accessor((row) => row?.per_piece_weight, {
    id: "Weight (grams)",
    cell: (info) => info.getValue(),
  }),
  columnHelper.accessor((row) => row?.incoming_remark, {
    id: "Incoming Remarks",
    cell: (info) => info.getValue(),
  }),
  columnHelper.accessor((row) => row?.part_id, {
    id: "In",
    enableColumnFilter: false,
    cell: (info) => (
      <InventoryInOutButton part_id={info.getValue()} type="in" />
    ),
  }),
  columnHelper.accessor((row) => row?.drawing_link, {
    id: "Link",
    enableColumnFilter: false,
    cell: (info) =>
      info.getValue() !== "" ? (
        <a href={info.getValue()} target="_blank" rel="noreferrer">
          <i className="bi bi-box-arrow-up-right"></i>
        </a>
      ) : (
        "NA"
      ),
  }),
  columnHelper.accessor((row) => row?.inprocess_link, {
    id: "Inprocess",
    enableColumnFilter: false,
    cell: (info) =>
      info.getValue() !== "" ? (
        <a href={info.getValue()} target="_blank" rel="noreferrer">
          <i className="bi bi-box-arrow-up-right inprocess-link"></i>
        </a>
      ) : (
        "NA"
      ),
  }),
  columnHelper.accessor((row) => row?.part_id, {
    id: "History",
    enableColumnFilter: false,
    cell: (props) => (
      <InventoryHistoryButton
        modalId={"#historyModal"}
        identifierId={props.getValue()}
        modalTitle={`${props.row.original?.part_name} : ${props.row.original?.drawing}`}
      />
    ),
  }),
  columnHelper.accessor((row) => row?.part_id, {
    id: "Notes",
    enableColumnFilter: false,
    cell: (props) =>
      props.row.original?.notes ? (
        <InventoryHistoryButton
          modalId="#additionalInfoModal"
          additionalInfo={true}
          identifierId={props.getValue()}
          modalTitle={`Info: ${props.row.original?.drawing}`}
        />
      ) : (
        <div className="d-flex justify-content-center">-</div>
      ),
  }),
  columnHelper.accessor((row) => row?.current_quantity, {
    id: "Qty",
    enableColumnFilter: false,
    cell: (info) => info.getValue(),
  }),
  columnHelper.accessor((row) => row?.safety_stock, {
    id: "Safety",
    enableColumnFilter: false,
    cell: (info) => info.getValue(),
  }),
  columnHelper.accessor(
    (row) => {
      return row?.current_quantity - row?.safety_stock;
    },
    {
      id: "Difference",
      enableColumnFilter: false,
      cell: (props) => (
        <div
          className="custom-color-label"
          style={
            getStockColors(
              props.row.original.current_quantity || 0,
              props.row.original.safety_stock || 0
            ) as React.CSSProperties
          }
        >
          {props.getValue()}
        </div>
      ),
    }
  ),
  columnHelper.accessor((row) => row?.location, {
    id: "Location",
    enableColumnFilter: false,
    cell: (info) => (
      <div
        className="custom-color-label"
        style={
          {
            "--item-background-color": hexToRgba(info.getValue().color, 0.6),
            "--item-color": fontColorBasedonBg(info.getValue().color)
              ? "#000000"
              : "#FFFFFF",
          } as React.CSSProperties
        }
      >
        {info.getValue().label}
      </div>
    ),
  }),
  columnHelper.accessor((row) => row?.laser_cutting, {
    id: "LC?",
    enableColumnFilter: false,
    cell: (info) => trueFalseIcons(info.getValue()),
  }),
  columnHelper.accessor((row) => row?.powder_coating, {
    id: "PC?",
    enableColumnFilter: false,
    cell: (info) => trueFalseIcons(info.getValue()),
  }),
  columnHelper.accessor((row) => row?.plating, {
    id: "Pltg?",
    enableColumnFilter: false,
    cell: (info) => trueFalseIcons(info.getValue()),
  }),
  columnHelper.accessor((row) => row?.part_id, {
    id: "Edit",
    enableColumnFilter: false,
    cell: (info) => (
      <InventoryAddDeleteButton part_id={info.getValue()} btnType={"edit"} />
    ),
  }),
  columnHelper.accessor((row) => row?.part_id, {
    id: "Delete",
    enableColumnFilter: false,
    cell: (info) => (
      <InventoryAddDeleteButton part_id={info.getValue()} btnType={"delete"} />
    ),
  }),
];
